import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Truck, Clock, Package, CheckCircle, AlertTriangle, FileText, Printer } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function TruckDetailPage() {
  const { truck_id } = useParams();
  const navigate = useNavigate();
  const [truck, setTruck] = useState(null);
  const [activity, setActivity] = useState([]);
  const [pallets, setPallets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTruckDetails();
    // Refresh every 10 seconds for real-time updates
    const interval = setInterval(fetchTruckDetails, 10000);
    return () => clearInterval(interval);
  }, [truck_id]);

  const fetchTruckDetails = async () => {
    try {
      // Fetch truck details
      const trucksResponse = await axios.get(`${API}/api/warehouse/trucks`);
      const truckData = trucksResponse.data.find(t => t.truck_id === truck_id);
      setTruck(truckData);

      // Fetch related pallets and activity (would come from receipt/inventory APIs)
      // For now, we'll show placeholder data
      setActivity([
        { time: new Date().toISOString(), action: 'Truck checked in', user: truckData?.driver_name || 'Driver', status: 'completed' },
        { time: new Date(Date.now() - 600000).toISOString(), action: 'Trailer inspection passed', user: 'QA Inspector', status: 'completed' },
      ]);

      setPallets([
        { lp_number: 'LP-20251020-0001', item_name: 'Ground Beef - 80/20', quantity: 1000, status: 'unloading', qa_status: 'pending' },
        { lp_number: 'LP-20251020-0002', item_name: 'Flour Tortillas - 10"', quantity: 5000, status: 'received', qa_status: 'passed' },
      ]);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching truck details:', error);
      setLoading(false);
    }
  };

  const handleAction = async (action) => {
    console.log(`Action: ${action} for truck ${truck_id}`);
    // TODO: Implement action handlers
    alert(`${action} functionality coming soon!`);
  };

  if (loading || !truck) {
    return (
      <div className="p-6">
        <div className="text-center py-12">Loading truck details...</div>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: 'bg-blue-100 text-blue-700',
      checked_in: 'bg-green-100 text-green-700',
      loading: 'bg-yellow-100 text-yellow-700',
      loaded: 'bg-purple-100 text-purple-700',
      departed: 'bg-gray-100 text-gray-700'
    };
    return (
      <span className={`px-3 py-1 rounded text-sm font-semibold ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/warehouse/trucks')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Truck className="w-8 h-8" />
              {truck.confirmation_number}
            </h1>
            <p className="text-gray-600 mt-1">{truck.carrier} - {truck.purpose}</p>
          </div>
        </div>
        <div>
          {getStatusBadge(truck.status)}
        </div>
      </div>

      {/* Appointment Overview */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4">Appointment Details</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <div className="text-sm text-gray-500">Date & Time</div>
            <div className="font-semibold flex items-center gap-1 mt-1">
              <Clock className="w-4 h-4 text-gray-400" />
              {new Date(truck.appointment_date).toLocaleDateString()} {truck.appointment_time}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Carrier</div>
            <div className="font-semibold mt-1">{truck.carrier}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">{truck.purpose === 'delivery' ? 'PO #' : 'SO #'}</div>
            <div className="font-semibold mt-1">{truck.linked_po_number || truck.linked_so_number || '-'}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Dock Door</div>
            <div className="font-semibold mt-1">{truck.dock_door_id || 'Not assigned'}</div>
          </div>
          {truck.driver_name && (
            <>
              <div>
                <div className="text-sm text-gray-500">Driver</div>
                <div className="font-semibold mt-1">{truck.driver_name}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">License Plate</div>
                <div className="font-semibold font-mono mt-1">{truck.license_plate || '-'}</div>
              </div>
            </>
          )}
        </div>
        {truck.notes && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
            <div className="text-sm font-semibold text-yellow-900">Notes:</div>
            <div className="text-sm text-yellow-800">{truck.notes}</div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4">Actions</h2>
        <div className="flex flex-wrap gap-3">
          {truck.status === 'scheduled' && (
            <button
              onClick={() => handleAction('check-in')}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <CheckCircle className="w-5 h-5" />
              Check In Truck
            </button>
          )}
          
          {truck.status === 'checked_in' && (
            <>
              <button
                onClick={() => handleAction('start-unload')}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Package className="w-5 h-5" />
                Start Unloading
              </button>
              <button
                onClick={() => handleAction('trailer-inspection')}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <FileText className="w-5 h-5" />
                Trailer Inspection
              </button>
            </>
          )}

          {truck.status === 'loading' && (
            <button
              onClick={() => handleAction('complete-loading')}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <CheckCircle className="w-5 h-5" />
              Complete Loading
            </button>
          )}

          {truck.status === 'loaded' && (
            <>
              <button
                onClick={() => handleAction('generate-bol')}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <FileText className="w-5 h-5" />
                Generate BOL
              </button>
              <button
                onClick={() => handleAction('print-bol')}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <Printer className="w-5 h-5" />
                Print BOL
              </button>
              <button
                onClick={() => handleAction('depart')}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Truck className="w-5 h-5" />
                Mark Departed
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Real-time Activity */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4">Real-Time Activity</h2>
          <div className="space-y-3">
            {activity.length > 0 ? (
              activity.map((item, index) => (
                <div key={index} className="flex items-start gap-3 p-3 border rounded">
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    item.status === 'completed' ? 'bg-green-500' : 'bg-yellow-500'
                  }`}></div>
                  <div className="flex-1">
                    <div className="font-semibold">{item.action}</div>
                    <div className="text-sm text-gray-600">{item.user}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      {new Date(item.time).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-8">No activity yet</div>
            )}
          </div>
        </div>

        {/* Pallets Status */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4">Pallets Status</h2>
          <div className="space-y-3">
            {pallets.length > 0 ? (
              pallets.map((pallet, index) => (
                <div key={index} className="border rounded p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-mono text-sm font-semibold text-blue-600">
                        {pallet.lp_number}
                      </div>
                      <div className="font-semibold mt-1">{pallet.item_name}</div>
                      <div className="text-sm text-gray-600">Quantity: {pallet.quantity}</div>
                    </div>
                    <div className="text-right">
                      <div className={`text-xs px-2 py-1 rounded ${
                        pallet.status === 'received' ? 'bg-green-100 text-green-700' :
                        pallet.status === 'unloading' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {pallet.status.toUpperCase()}
                      </div>
                      <div className={`text-xs px-2 py-1 rounded mt-1 ${
                        pallet.qa_status === 'passed' ? 'bg-green-100 text-green-700' :
                        pallet.qa_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        QA: {pallet.qa_status.toUpperCase()}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-8">No pallets tracked yet</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
